import type { CoverageType } from "@/lib/submissions/coverageTerms";
import { useEffect, useId, useState, type ReactNode } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { ArrowLeft, X } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { ActionsMenu, type BaseFormAction } from "@/base/ActionsMenu";
import { buttonClass, TOUCH_SQUARE } from "@/components/buttonStyles";
import { CarrierIdentity } from "@/components/CarrierIdentity";
import { BindQuoteStep } from "@/components/submissions/BindQuoteStep";
import { QuoteStep } from "@/components/submissions/QuoteStep";
import { QuoteSelection } from "@/components/submissions/QuoteBinding";
import { QuoteChipBadge } from "@/components/submissions/QuoteCard";
import { QuoteActionItems } from "@/components/submissions/QuoteActionItems";
import {
  ADD_NOTE_LABEL,
  AddQuoteNoteDialog,
  QuoteNotes,
} from "@/components/submissions/QuoteNotes";
import { RequestManualQuoteDialog } from "@/components/submissions/RequestManualQuote";
import { RequestUnderwriterReviewDialog } from "@/components/submissions/RequestUnderwriterReview";
import {
  DetailAction,
  QuoteDetail,
} from "@/components/submissions/QuoteDetail";
import {
  getQuoteDocument,
  type BrokerQuote,
  type MissingField,
  type CarrierDecline,
  type OfferTermsGroupView,
} from "@/lib/submissions/brokerSubmissions";
import { saveFile } from "@/lib/downloadFile";
import { messageFor } from "@/lib/errors";
import type { QuoteCard } from "@/lib/submissions/quoteCards";
import {
  offersQuoteDocument,
  QUOTE_DOCUMENT_LABELS,
  quoteDocumentFallback,
} from "@/lib/submissions/quoteDocument";
import type { QuoteNote } from "@/lib/submissions/quoteConditions";
import {
  canAskForReview,
  REVIEW_ACTION_LABEL,
} from "@/lib/submissions/quoteReview";

// BRK-S347 — the quote popup: one carrier's offer over the cards, at most
// 768px wide, full screen on a phone. It opens on the offer's details; its
// action turns it to the carrier's questions, then its price. ✕, Escape,
// "Back to quotes" and the browser's Back all close it (the URL holds it, see
// useQuotePopup), and a run goes on after it closes. A backdrop click is
// ignored while the questions are up, so a stray click drops no answers. A
// Workers' Comp submission's popup asks no questions (BRK-S393): they're the
// wizard's last step's, and the popup shows quotes only.
//
// BRK-S465 — a quote the broker works on has three tabs: Overview (its
// details), Notes, and Actions (what Signal works for the broker on it,
// PMS-S872). The header's Actions menu holds what the broker sends Signal,
// each in its own dialog: Request Underwriter Review where they can ask, and
// Add Note.
//
// BRK-S509 — and Download Quote (Download Indication on an indication): Signal's
// PDF of a priced offer (PMS-S932), saved under PMS's name, to share with the
// insured. Busy while it fetches; a failure is a line under the carrier.

function Frame({
  titleId,
  onClose,
  dismissable,
  header,
  tabs,
  body,
  footer,
}: {
  titleId: string;
  onClose: () => void;
  /** Whether a backdrop click closes it. */
  dismissable: boolean;
  header: ReactNode;
  /** The tab bar, under the header so it stays put as the body scrolls. */
  tabs?: ReactNode;
  body: ReactNode;
  footer: ReactNode;
}) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)", {
    noSsr: true,
  });
  return (
    <Dialog
      open
      onClose={(_event, reason) => {
        if (reason === "backdropClick" && !dismissable) return;
        onClose();
      }}
      aria-labelledby={titleId}
      fullWidth
      maxWidth="md"
      fullScreen={phone}
      scroll="paper"
      transitionDuration={reduceMotion ? 0 : undefined}
      // With tabs, the popup keeps its size from tab to tab, however little
      // one holds (Nav, BRK-S465): the full height a dialog may take, the
      // tab scrolling inside it. A phone's is full screen already.
      slotProps={
        tabs && !phone
          ? { paper: { sx: { height: "calc(100% - 64px)" } } }
          : undefined
      }
    >
      <div className="flex items-start gap-3 border-b border-brk-border p-4">
        <div className="flex min-w-0 flex-1 items-start gap-3">{header}</div>
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className={`rounded-md p-2 text-brk-dim hover:bg-brk-bg hover:text-brk-text ${TOUCH_SQUARE}`}
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>
      {tabs && <div className="border-b border-brk-border">{tabs}</div>}
      {/* Room for the scrollbar either way, so a tab's content keeps its
          width whether or not it scrolls. */}
      <DialogContent sx={{ p: { xs: 2, sm: 3 }, scrollbarGutter: "stable" }}>
        {body}
      </DialogContent>
      <div className="flex flex-col gap-3 border-t border-brk-border p-4 sm:flex-row sm:items-end sm:justify-between">
        <button
          type="button"
          onClick={onClose}
          className={`${buttonClass({ variant: "secondary" })} self-start sm:self-auto`}
        >
          <ArrowLeft aria-hidden="true" className="h-4 w-4" />
          Back to quotes
        </button>
        {footer}
      </div>
    </Dialog>
  );
}

type QuoteTab = "overview" | "notes" | "actions";
const QUOTE_TABS: readonly { id: QuoteTab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "notes", label: "Notes" },
  { id: "actions", label: "Actions" },
];

export function QuotePopup({
  submissionId,
  accountId = null,
  onQuotesChanged,
  card,
  pending,
  termGroups,
  coverageTypes,
  floorMissing,
  pulling,
  onClose,
  onFirmUp,
  onComplete,
  onRetry,
  onWatch,
  onUpdate,
  onCompleteProfile,
  missingFieldHref,
  onDeclineChange,
  asks = true,
}: {
  submissionId: string;
  /** The insured's account, whose documents serve a subjectivity's files. */
  accountId?: string | null;
  /** Reads the quotes again, after a subjectivity's answer or a note. */
  onQuotesChanged?: () => void | Promise<void>;
  /** The offer the URL opens; null when this visit has none by that key. */
  card: QuoteCard | null;
  /** The markets are still being checked, so the offer may yet appear. */
  pending: boolean;
  termGroups: OfferTermsGroupView[];
  coverageTypes?: CoverageType[];
  floorMissing: MissingField[];
  pulling: boolean;
  onClose: () => void;
  /** Start (or continue, or try again) this carrier's quote. */
  onFirmUp: (card: QuoteCard) => void;
  onComplete: (card: QuoteCard) => Promise<void>;
  onRetry: (card: QuoteCard) => void;
  onWatch: (key: string | null) => void;
  onUpdate: () => void;
  onCompleteProfile?: () => void;
  missingFieldHref?: (field: MissingField) => string | null;
  onDeclineChange: (
    card: QuoteCard,
    declined: CarrierDecline | undefined,
  ) => void;
  /** Whether the popup asks a carrier's questions; false when the wizard's
   *  last step asks them (BRK-S393). */
  asks?: boolean;
}) {
  const titleId = useId();
  const { identity, authedBlob } = useAuth();
  const [view, setView] = useState<"detail" | "firm" | "bind">("detail");
  const tabsId = useId();
  const [tab, setTab] = useState<QuoteTab>("overview");
  // Actions reads PMS once it's first shown, then stays mounted.
  const [actionsShown, setActionsShown] = useState(false);
  const [dialog, setDialog] = useState<"review" | "note" | "manual" | null>(
    null,
  );
  // Notes PMS kept this visit (Add Note), until the quotes are read again.
  const [addedNotes, setAddedNotes] = useState<QuoteNote[]>([]);
  const notes = card?.quote?.notes;
  useEffect(() => setAddedNotes([]), [notes]);

  const [downloading, setDownloading] = useState(false);
  const [downloadProblem, setDownloadProblem] = useState<string | null>(null);

  function show(next: QuoteTab) {
    setTab(next);
    if (next === "actions") setActionsShown(true);
  }

  async function download(quote: BrokerQuote) {
    setDownloading(true);
    setDownloadProblem(null);
    try {
      saveFile(
        await getQuoteDocument(authedBlob, submissionId, quote.id),
        quoteDocumentFallback(quote),
      );
    } catch (err) {
      // A handled line, never a throw or a download that didn't happen (NN #7).
      setDownloadProblem(`Didn’t download: ${messageFor(err)}`);
    } finally {
      setDownloading(false);
    }
  }

  if (!card) {
    return (
      <Frame
        titleId={titleId}
        onClose={onClose}
        dismissable
        header={
          <h2 id={titleId} className="text-heading text-brk-text">
            Quote
          </h2>
        }
        body={
          pending ? (
            <p role="status" className="text-sm text-brk-dim">
              Getting price indications…
            </p>
          ) : (
            <p className="text-sm text-brk-text">
              This quote isn’t in this submission’s current results.
            </p>
          )
        }
        footer={
          pending ? null : (
            <button
              type="button"
              onClick={onUpdate}
              disabled={pulling}
              className={buttonClass()}
            >
              Check the markets again
            </button>
          )
        }
      />
    );
  }

  const firm = view === "firm";
  // The bind view needs PMS's answer read back, and a quote to bind.
  const binding = view === "bind" && card.quote !== null && onQuotesChanged;
  // A quote the broker works on, on its details: not over its questions or
  // the bind form.
  const working =
    view === "detail" && card.quote !== null && onQuotesChanged
      ? { quote: card.quote, onChanged: onQuotesChanged }
      : null;
  const menu: BaseFormAction[] = working
    ? [
        ...(canAskForReview(card, identity)
          ? [
              {
                id: "review",
                label: REVIEW_ACTION_LABEL,
                onSelect: () => setDialog("review"),
              },
            ]
          : []),
        ...(working.quote.notes
          ? [
              {
                id: "note",
                label: ADD_NOTE_LABEL,
                onSelect: () => setDialog("note"),
              },
            ]
          : []),
        ...(offersQuoteDocument(card)
          ? [
              {
                id: "document",
                label: QUOTE_DOCUMENT_LABELS[working.quote.kind],
                disabled: downloading,
                onSelect: () => void download(working.quote),
              },
            ]
          : []),
      ]
    : [];
  const tabs = working
    ? QUOTE_TABS.filter((t) => t.id !== "notes" || working.quote.notes)
    : [];
  const detail = (
    <QuoteDetail
      card={card}
      termGroups={termGroups}
      coverageTypes={coverageTypes}
      missingFieldHref={missingFieldHref}
      conditions={
        onQuotesChanged
          ? { submissionId, accountId, onChanged: onQuotesChanged }
          : undefined
      }
    />
  );
  return (
    <Frame
      titleId={titleId}
      onClose={onClose}
      // A stray click drops no answers, the carrier's or the bind's.
      dismissable={view === "detail"}
      header={
        <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <CarrierIdentity
                name={card.carrier}
                profile={card.carrierProfile}
                nameId={titleId}
                heading
              />
              <QuoteChipBadge chip={card.chip} />
            </div>
            {card.quote?.carrierQuoteNumber && (
              <span className="break-all tabular-nums text-xs text-brk-dim">
                Quote {card.quote.carrierQuoteNumber}
              </span>
            )}
            {downloading && (
              <p role="status" className="text-meta text-brk-dim">
                Downloading…
              </p>
            )}
            {downloadProblem && (
              <p role="alert" className="text-meta text-brk-danger">
                {downloadProblem}
              </p>
            )}
          </div>
          {menu.length > 0 && (
            <div className="shrink-0 self-start">
              <ActionsMenu actions={menu} />
            </div>
          )}
          {/* A carrier Signal quotes by hand (BRK-S395): mounted while it is
              one, so the answer stays up after the read again. */}
          {card.column.manual && card.column.carrierId && onQuotesChanged && (
            <RequestManualQuoteDialog
              submissionId={submissionId}
              carrierId={card.column.carrierId}
              carrier={card.carrier}
              open={dialog === "manual"}
              onClose={() => setDialog(null)}
              onChanged={onQuotesChanged}
            />
          )}
          {/* Mounted while the broker works on the quote, so an answer stays
              up once the quotes, read again, no longer offer the action. */}
          {working && (
            <>
              <RequestUnderwriterReviewDialog
                submissionId={submissionId}
                quote={working.quote}
                carrier={card.carrier}
                open={dialog === "review"}
                onClose={() => setDialog(null)}
                onChanged={working.onChanged}
              />
              {working.quote.notes && (
                <AddQuoteNoteDialog
                  submissionId={submissionId}
                  quoteId={working.quote.id}
                  open={dialog === "note"}
                  onClose={() => setDialog(null)}
                  onAdded={(note) => {
                    setAddedNotes((all) => [note, ...all]);
                    show("notes");
                  }}
                  onChanged={working.onChanged}
                />
              )}
            </>
          )}
        </div>
      }
      tabs={
        tabs.length > 0 && (
          <Tabs
            value={tab}
            onChange={(_, next: QuoteTab) => show(next)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label={`${card.carrier} quote sections`}
            sx={{ px: { xs: 1, sm: 2 } }}
          >
            {tabs.map((t) => (
              <Tab
                key={t.id}
                value={t.id}
                label={t.label}
                id={`${tabsId}-tab-${t.id}`}
                aria-controls={`${tabsId}-panel-${t.id}`}
              />
            ))}
          </Tabs>
        )
      }
      body={
        binding && card.quote ? (
          <BindQuoteStep
            submissionId={submissionId}
            quote={card.quote}
            carrier={card.carrier}
            onBack={() => setView("detail")}
            onChanged={onQuotesChanged}
          />
        ) : firm ? (
          <QuoteStep
            submissionId={submissionId}
            card={card}
            onBack={() => setView("detail")}
            onComplete={() => onComplete(card)}
            onRetry={() => onRetry(card)}
            onWatch={(watching) => onWatch(watching ? card.key : null)}
            onCompleteProfile={onCompleteProfile}
            missingFieldHref={missingFieldHref}
            onDeclineChange={(declined) => onDeclineChange(card, declined)}
            asks={asks}
          />
        ) : working ? (
          // Overview and Notes stay mounted, hidden when not shown, so a tab
          // switch loses nothing; Actions once it's been shown.
          tabs.map((t) => (
            <div
              key={t.id}
              role="tabpanel"
              id={`${tabsId}-panel-${t.id}`}
              aria-labelledby={`${tabsId}-tab-${t.id}`}
              hidden={t.id !== tab}
            >
              {t.id === "overview" && detail}
              {t.id === "notes" && working.quote.notes && (
                <QuoteNotes notes={working.quote.notes} added={addedNotes} />
              )}
              {t.id === "actions" && actionsShown && (
                <QuoteActionItems
                  submissionId={submissionId}
                  quote={working.quote}
                />
              )}
            </div>
          ))
        ) : (
          detail
        )
      }
      footer={
        firm || binding ? null : (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
            {card.quote && onQuotesChanged && (
              <QuoteSelection
                submissionId={submissionId}
                quote={card.quote}
                onChanged={onQuotesChanged}
              />
            )}
            <DetailAction
              card={card}
              floorMissing={floorMissing}
              pulling={pulling}
              onUpdate={onUpdate}
              onCompleteProfile={onCompleteProfile}
              onFirmUp={() => {
                onFirmUp(card);
                setView("firm");
              }}
              onBind={onQuotesChanged ? () => setView("bind") : undefined}
              onRequestQuote={
                card.column.manual && onQuotesChanged
                  ? () => setDialog("manual")
                  : undefined
              }
              onRequestReview={working ? () => setDialog("review") : undefined}
              asks={asks}
            />
          </div>
        )
      }
    />
  );
}
